import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, switchMap, tap, throwError } from "rxjs";
import { IAppInfo} from "../models/app.model";
import { HttpErrorResponse } from "@angular/common/http";
import { AuthService } from "@app/core/services/auth.service";
import { HttpService } from "@app/core/services/http.service";
import { MODEL_CONFIG } from "@app/core/config/model.config";
import { RESTRICTED_DEPARTMENT_CODES, IRKESHTAM, DOSTUK_KEN_SAI } from "@app/core/config/global.config";

@Injectable({
  providedIn: 'root'
})

export class AppService {

  private appInfoSource = new BehaviorSubject<IAppInfo>(<IAppInfo>{
    "user.technical": false,
    "user.name": "",
    "application.description": "",
    "api.pagination.max-per-page": 0,
    "application.sdk": "",
    "user.navigator": "",
    "application.help": "",
    "user.singleTab": false,
    "view.max-tabs": "",
    "application.author": "",
    "data.upload.max-size": "",
    "user.login": "",
    "user.group": "",
    "application.mode": "",
    "user.viewCustomizationPermission": 0,
    "api.pagination.default-per-page": 0,
    "application.logo": "",
    "user.id": 0,
    "user.canViewCollaboration": false,
    "application.version": "",
    "user.action": "",
    "application.home": "",
    "user.nameField": "",
    "user.lang": "",
    "application.copyright": "",
    "view.menubar.location": "",
    "application.themes": [],
    "user.noHelp": false,
    "application.name": "",
  });

  public appInfo$ = this.appInfoSource.asObservable();
  private serverAvailableSource$ = new BehaviorSubject<boolean>(false);

  private isRestrictedGroupSource = new BehaviorSubject<boolean>(false);
  public isRestrictedGroup$ = this.isRestrictedGroupSource.asObservable();

  private isIrkeshtamSource = new BehaviorSubject<boolean>(false);
  public isIrkeshtam$ = this.isIrkeshtamSource.asObservable();

  private dostukKenSaiSource = new BehaviorSubject<boolean>(false);
  public dostukKenSai$ = this.dostukKenSaiSource.asObservable();

  constructor(
    private authService: AuthService,
    private httpService: HttpService
  ) { }

  checkServer(): Observable<boolean> {
    return this.httpService.getAppInfo<IAppInfo>().pipe(
      switchMap(response => {
        const isAvailable = [200, 201, 204, 304].includes(response.status);
        const appInfo = response.body;

        if (!isAvailable || !appInfo?.["user.id"]) {
          this.serverAvailableSource$.next(false);
          this.authService.onLogout();
          return of(false);
        }

        this.appInfoSource.next(appInfo);
        this.serverAvailableSource$.next(true);

        const userId = appInfo["user.id"];

        return this.getUserInfo(userId).pipe(
          tap(userInfo => {
            const groupCode = userInfo?.employee?.secondaryCustomsDepartment?.code;
            const isAllowed = !RESTRICTED_DEPARTMENT_CODES.includes(groupCode);
            const isIrkeshtam = IRKESHTAM.includes(groupCode);
            const isDostukKenSai = DOSTUK_KEN_SAI.includes(groupCode)

            this.isRestrictedGroupSource.next(isAllowed);
            this.isIrkeshtamSource.next(isIrkeshtam);
            this.dostukKenSaiSource.next(isDostukKenSai);
          }),
          map(() => true),
          catchError(err => {
            console.error('Ошибка при получении userInfo:', err);
            this.authService.onLogout();
            return of(false);
          })
        );
      }),
      catchError((error: HttpErrorResponse) => {
        this.serverAvailableSource$.next(false);
        this.authService.onLogout();
        return of(false);
      })
    );
  }

  getAppInfoValue(key: keyof IAppInfo): Observable<any> {
    return this.appInfo$.pipe(
      map( appInfo => appInfo[key] )
    );
  }

  getUserInfo(userId: number): Observable<any> {
    const params = {
      fields: MODEL_CONFIG.USER.FIELDS
    };
    return this.httpService.getFetchRecord(MODEL_CONFIG.USER.MODEL, userId.toString(), params)
      .pipe(
        map((response: any) => {
          if (response.status === -1) {
            throw new Error(response.data?.message);
          }
          return response.data[0];
        }),
        catchError((error) => throwError(() => error))
        );
  }

}
